import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { chatApi } from '../utils/apiUtils';
import MarkdownRenderer from './MarkdownRenderer';

// Chat-with-paper UI.
//
// Mount-time behavior:
//   1. If the user already has a session on this paper (server-side check
//      in createOrGetSession returns the existing row), we load it and its
//      message history.
//   2. Otherwise the user must pass the ai-chat eligibility gate. That gate
//      is enforced server-side; we also surface it client-side so the panel
//      can render a disabled state with a helpful reason instead of making
//      a doomed POST.
//
// Once a session is open, sending messages does not re-check eligibility —
// the user has already been counted for this paper.

const ChatPanel = ({ paperId }) => {
  const { user, eligibility, aiChattedPaperIds, refreshUser } = useAuth();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [streamingAssistant, setStreamingAssistant] = useState('');
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState('');
  const [bootstrapping, setBootstrapping] = useState(false);
  const [bootstrapAttempted, setBootstrapAttempted] = useState(false);
  const scrollRef = useRef(null);

  const alreadyCounted = aiChattedPaperIds.includes(paperId);
  const canOpen = !!user && (alreadyCounted || eligibility.aiChatAllowed);
  const notAllowedReason = !user
    ? 'Sign in to chat with papers'
    : eligibility.aiChatNotAllowedReason;

  // Auto-scroll the message list on new content.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streamingAssistant]);

  // Bootstrap / resume the session once the paper + user are known.
  useEffect(() => {
    if (!paperId || !canOpen || bootstrapAttempted || bootstrapping) return;
    let cancelled = false;
    setBootstrapping(true);
    setError('');
    (async () => {
      try {
        const data = await chatApi.createOrGetSession(paperId);
        if (cancelled) return;
        setSession(data.session);
        setMessages(data.messages || []);
        // A brand-new session means server-side usage just got bumped.
        // Pull the user row so eligibility reflects the new count.
        if (!alreadyCounted) {
          refreshUser();
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.message || 'Failed to open chat session');
        }
      } finally {
        if (!cancelled) {
          setBootstrapping(false);
          setBootstrapAttempted(true);
        }
      }
    })();
    return () => { cancelled = true; };
    // alreadyCounted intentionally omitted — it flips after refreshUser()
    // but we don't want to re-bootstrap on that transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paperId, canOpen, bootstrapAttempted]);

  const handleSend = useCallback(async () => {
    const content = input.trim();
    if (!content || !session || isStreaming) return;
    setInput('');
    setError('');
    // Optimistic: show the user turn immediately with a temporary id.
    const optimisticId = `temp-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: optimisticId, role: 'user', content, createdAt: new Date().toISOString() }
    ]);
    setStreamingAssistant('');
    setIsStreaming(true);

    let assembled = '';
    try {
      await chatApi.streamMessage(session.id, content, {
        onChunk: (data) => {
          if (!data?.text) return;
          assembled += data.text;
          setStreamingAssistant(assembled);
        },
        onDone: (data) => {
          setMessages((prev) => [
            ...prev,
            {
              id: data?.messageId || `assistant-${Date.now()}`,
              role: 'assistant',
              content: data?.content || assembled,
              createdAt: new Date().toISOString()
            }
          ]);
          setStreamingAssistant('');
        },
        onError: (err) => {
          setError(err?.message || 'Chat failed');
        }
      });
    } catch (err) {
      setError(err?.message || 'Chat failed');
    } finally {
      setIsStreaming(false);
    }
  }, [input, session, isStreaming]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!canOpen) {
    return (
      <div className="rounded-xl border border-gray-100 p-5 bg-gray-50">
        <h3 className="text-xs font-bold text-gray-900 uppercase tracking-widest mb-2">
          Chat with this paper
        </h3>
        <p className="text-xs text-gray-500 italic">{notAllowedReason}</p>
      </div>
    );
  }

  if (bootstrapping) {
    return (
      <div className="rounded-xl border border-gray-100 p-5 bg-white">
        <h3 className="text-xs font-bold text-gray-900 uppercase tracking-widest mb-2">
          Chat with this paper
        </h3>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <div className="w-3 h-3 border-2 border-gray-200 border-t-purple-500 rounded-full animate-spin" />
          Extracting paper content...
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-pink-50">
        <h3 className="text-xs font-bold text-gray-900 uppercase tracking-widest">
          Chat with this paper
        </h3>
      </div>

      <div
        ref={scrollRef}
        className="max-h-80 overflow-y-auto px-5 py-4 space-y-3"
      >
        {messages.length === 0 && !streamingAssistant && (
          <p className="text-xs text-gray-400 italic">
            Ask a question about this paper to get started.
          </p>
        )}
        <AnimatePresence initial={false}>
          {messages.map((m) => (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] px-3.5 py-2 rounded-2xl text-xs leading-relaxed ${
                  m.role === 'user'
                    ? 'bg-purple-600 text-white rounded-br-sm whitespace-pre-wrap'
                    : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                }`}
              >
                {m.role === 'assistant' ? (
                  <MarkdownRenderer content={m.content} compact />
                ) : (
                  m.content
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        {streamingAssistant && (
          <div className="flex justify-start">
            <div className="max-w-[85%] px-3.5 py-2 rounded-2xl rounded-bl-sm bg-gray-100 text-gray-800 text-xs leading-relaxed">
              <MarkdownRenderer content={streamingAssistant} compact />
              <span className="inline-block w-1.5 h-3 ml-0.5 bg-purple-500 align-middle animate-pulse" />
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="px-5 pt-2 text-[11px] text-red-500">{error}</div>
      )}

      <div className="px-5 py-3 border-t border-gray-100 flex items-end gap-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about the methodology, results, limitations..."
          rows={1}
          disabled={isStreaming}
          className="flex-1 resize-none rounded-lg border border-gray-200 px-3 py-2 text-xs text-gray-800 focus:outline-none focus:border-purple-400 disabled:bg-gray-50"
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={isStreaming || !input.trim()}
          className="inline-flex items-center px-3.5 py-2 text-[11px] font-semibold text-white bg-purple-600 rounded-lg disabled:opacity-40 hover:bg-purple-700 transition-colors"
        >
          {isStreaming ? 'Thinking...' : 'Send'}
        </button>
      </div>
    </div>
  );
};

export default ChatPanel;
